import { useEditorState } from "@handlewithcare/react-prosemirror";
import { useMemo, useState } from "react";
import { useTimeoutFn } from "react-use";

import { getHeadingAnchorTag } from "../../../../../../api-collab/lib/prosemirror-schema/nodes/heading";

/**
 * Creates a table of conntents from the headings in the editor state of the current document. As
 * the user scrolls, the current heading is highlighted.
 */
export const useTableOfContents = () => {
	const state = useEditorState();
	const [visibleHeading, setVisibleHeading] = useState<string | null>(null);
	const headings = useMemo(() => {
		if (!state?.doc) {
			return [];
		}

		const headers: [number, string, string][] = [];
		for (let i = 0; i < state.doc.childCount; i++) {
			const node = state.doc.child(i);
			if (node.type.name === "heading") {
				headers.push([node.attrs["level"], node.textContent, getHeadingAnchorTag(node)]);
			}
		}
		return headers;
	}, [state?.doc]);

	useTimeoutFn(() => {
		const observer = new IntersectionObserver(
			(headings) => {
				const firstHeading = headings.find((heading) => heading.isIntersecting);
				if (!firstHeading) {
					return;
				}

				setVisibleHeading(firstHeading.target.id);
			},
			{
				root: document.body,

				// IMPORTANT: 70% means that we highlight the heading in the table of contents when
				// even 1px of it is visible 70% from the bottom. This is a stupid heuristic, and
				// I'm not sure if we will replace it, but it seems to work for now.
				rootMargin: "0px 0px -70% 0px",
				threshold: 0,
			}
		);

		for (const heading of headings) {
			const element = document.getElementById(heading[2]);
			if (!element) {
				return;
			}

			observer.observe(element);
		}

		return () => observer.disconnect();
	}, 1000);

	return { headings, visibleHeading };
};
